import React from 'react';

import { UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { MembershipTypeFormSchemaValues } from 'src/features/membershipType/formSchema';
import { MEMBERSHIP_TEMPLATE_CATEGORY } from 'src/features/membershipType/types';
import { membershipTypeMessages } from 'src/features/membershipType/utils/messages';
import { useListPricingPlans } from 'src/features/pricingPlan/api/useListPricingPlans';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { PRICING_PLAN_STATUS } from 'src/features/pricingPlan/utils/types';
import { visitorMessages } from 'src/features/visitor/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import { SectionBlock, SectionBlockSubtitle, SectionBlockTitle } from 'src/registry/components/blocks/SectionBlock';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import SwitchFormField from 'src/registry/components/forms/SwitchFormField';
import { Separator } from 'src/registry/ui/Separator';
import { WizardStepInfo } from 'src/registry/ui/Wizard';

type Props = {
    form: UseFormReturn<MembershipTypeFormSchemaValues>;
};

export default function PricingStep({ form }: Props) {
    const { formatMessage, locale } = useIntl();

    const { has_pricing_plan, category } = form.watch();
    const { parkingId } = useParking();
    const { data: pricingPlansData } = useListPricingPlans({
        parkingId,
        currently_active: true,
        status: PRICING_PLAN_STATUS.PUBLISHED,
    });

    const pricingPlanOptions = React.useMemo(() => {
        if (!pricingPlansData) return [];
        return pricingPlansData.results.map((plan) => {
            const translatedPlan = plan.translations.find((p) => p.language === locale);

            return { value: plan.identity, label: translatedPlan?.name || '' };
        });
    }, [pricingPlansData, locale]);

    return (
        <div className="flex gap-4">
            <div className="flex flex-2 flex-col gap-4">
                <SwitchFormField<MembershipTypeFormSchemaValues>
                    form={form}
                    name="has_pricing_plan"
                    label={formatMessage(membershipTypeMessages.hasPricingPlan)}
                    disabled={category === MEMBERSHIP_TEMPLATE_CATEGORY.SUBSCRIPTION}
                    description={formatMessage(membershipTypeMessages.hasPricingPlanDescription)}
                    tooltip={formatMessage(membershipTypeMessages.hasPricingPlanTooltip)}
                />
                <SelectFormField<MembershipTypeFormSchemaValues>
                    key={has_pricing_plan as null}
                    form={form}
                    variant="default"
                    name="pricing_plan_identity"
                    label={formatMessage(pricingPlanMessages.pricingPlan)}
                    options={pricingPlanOptions}
                    required={Boolean(has_pricing_plan)}
                    disabled={!has_pricing_plan}
                    description={formatMessage(membershipTypeMessages.pricingPlanDescription)}
                    tooltip={formatMessage(membershipTypeMessages.pricingPlanTooltip)}
                />
                <SectionBlock>
                    <Separator />
                    <div className="flex flex-col">
                        <SectionBlockTitle className="text-base">
                            {formatMessage(visitorMessages.subscription)}
                        </SectionBlockTitle>
                        <SectionBlockSubtitle>
                            {formatMessage(membershipTypeMessages.subscriptionTypeDescription)}
                        </SectionBlockSubtitle>
                    </div>
                    <SwitchFormField<MembershipTypeFormSchemaValues>
                        form={form}
                        name="apply_pricing_per_space"
                        label={formatMessage(membershipTypeMessages.applyPricingPerSpace)}
                        disabled={!has_pricing_plan || category !== MEMBERSHIP_TEMPLATE_CATEGORY.SUBSCRIPTION}
                        description={formatMessage(membershipTypeMessages.applyPricingPerSpaceDescription)}
                        tooltip={formatMessage(membershipTypeMessages.applyPricingPerSpaceTooltip)}
                    />
                    <SwitchFormField<MembershipTypeFormSchemaValues>
                        form={form}
                        name="auto_renew"
                        label={formatMessage(membershipTypeMessages.autoRenew)}
                        disabled={!has_pricing_plan || category !== MEMBERSHIP_TEMPLATE_CATEGORY.SUBSCRIPTION}
                        description={formatMessage(membershipTypeMessages.autoRenewDescription)}
                        tooltip={formatMessage(membershipTypeMessages.autoRenewTooltip)}
                    />
                </SectionBlock>
            </div>
            <div className="flex-1">
                <WizardStepInfo
                    header={formatMessage(membershipTypeMessages.price)}
                    body={formatMessage(membershipTypeMessages.priceBody)}
                    tip={formatMessage(membershipTypeMessages.priceTip)}
                />
            </div>
        </div>
    );
}
